<template>
    <div>
        <v-row class="mt-2">
            <v-col xl="6" md="10" sm="12" class="ml-5">
                <div class="">
                    <div class="px-5">
                        <v-row>
                            <v-col cols="12">
                                <h1 style="font-weight: 300" class="pb-2">New Content Project</h1>
                                <p style="max-width: 800px">
                                    Use the Content Project feature to create new or optimize existing content pieces or landing pages. Everything starts with your project title and your main keyword.
                                    Make sure to select the right location and device you are focusing on.
                                </p>
                            </v-col>
                        </v-row>
                    </div>

                    <!-- Choice: create vs. optimize -->
                    <div class="flex">
                        <div class="mx-auto" role="heading" style="font-size: 18px">What do you want to do?</div>
                    </div>

                    <v-row class="choose" role="radiogroup" aria-label="Choose content workflow">
                        <v-col>
                            <!-- Turn card into a real button for keyboard users -->
                            <button
                                class="choice-btn"
                                :class="!addOwnURL ? 'quaroshadow newcontent active ml-auto' : 'ml-auto newcontent'"
                                type="button"
                                @click="addOwnURL = false"
                                :aria-pressed="!addOwnURL"
                                aria-label="Create new content">
                                <div class="choosetitle pa-auto" style="max-width: 59%">Create new Content</div>
                                <div class="chooseimage">
                                    <img v-if="!addOwnURL" src="../../../assets/newwhite.svg" width="90%" class="mx-auto" alt="Create new content selected" />
                                    <img v-else src="../../../assets/content_project.svg" width="90%" class="mx-auto" alt="Create new content" />
                                </div>
                            </button>
                        </v-col>

                        <v-col>
                            <button
                                class="choice-btn"
                                :class="addOwnURL ? 'quaroshadow optimizecontent active mr-auto' : 'optimizecontent mr-auto'"
                                type="button"
                                @click="addOwnURL = true"
                                :aria-pressed="addOwnURL"
                                aria-label="Optimize existing content">
                                <div class="choosetitle pa-auto"><span>Optimize existing Content</span></div>
                                <div class="chooseimage">
                                    <img src="../../../assets/opimize_primary.svg" width="90%" class="mx-auto" alt="Optimize existing content" />
                                </div>
                            </button>
                        </v-col>
                    </v-row>

                    <div class="px-5">
                        <!-- PROJECT TITLE -->
                        <div class="pb-4 qgreycard">
                            <div class="lbl pb-2" style="font-size: 14px !important">
                                PROJECT TITLE
                                <v-tooltip max-width="250" top open-on-focus>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-on="on"
                                            v-bind="attrs"
                                            icon
                                            type="button"
                                            x-small
                                            class="pl-0 mr-auto tooltipicon"
                                            aria-label="Help: Project title"
                                            :aria-describedby="'tt-project-title'">
                                            <v-icon small color="grey darken-2" aria-hidden="true">mdi-help-circle-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <span id="tt-project-title" role="tooltip"> The "Project Title" field specifies the title of your content project. </span>
                                </v-tooltip>
                            </div>

                            <v-text-field
                                @focus="showTipp('title')"
                                type="text"
                                rounded
                                filled
                                v-model="project.title"
                                hint="e.g., Optimization: KW Research"
                                persistent-hint
                                :aria-describedby="'tt-project-title'" />
                        </div>

                        <v-row no-gutters>
                            <!-- TOPIC -->
                            <v-col class="cols-6">
                                <div class="pb-4 qgreycard mt-0">
                                    <div class="lbl pb-2" style="font-size: 14px !important">
                                        TOPIC
                                        <v-tooltip max-width="250" top open-on-focus>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn v-on="on" v-bind="attrs" icon type="button" x-small class="pl-0 mr-auto tooltipicon" aria-label="Help: Topic" :aria-describedby="'tt-topic'">
                                                    <v-icon small color="grey darken-2" aria-hidden="true">mdi-help-circle-outline</v-icon>
                                                </v-btn>
                                            </template>
                                            <span id="tt-topic" role="tooltip">Enter any topic (phrase or keyword) you want to write about.</span>
                                        </v-tooltip>
                                    </div>

                                    <v-text-field
                                        :disabled="disabled"
                                        @focus="showTipp('main_keyword')"
                                        @blur="validateMainKeyword"
                                        class="mb-2"
                                        rounded
                                        filled
                                        v-model="project.main_keyword"
                                        hint="e.g., keyword research"
                                        persistent-hint
                                        :aria-describedby="'tt-topic'" />
                                </div>
                            </v-col>

                            <!-- LOCATION -->
                            <v-col class="cols-6">
                                <div class="pb-6 qgreycard mt-0">
                                    <div class="lbl pb-2" style="font-size: 14px !important">
                                        LOCATION
                                        <v-tooltip max-width="250" top open-on-focus>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn
                                                    v-on="on"
                                                    v-bind="attrs"
                                                    icon
                                                    type="button"
                                                    x-small
                                                    class="pl-0 mr-auto tooltipicon"
                                                    aria-label="Help: Location"
                                                    :aria-describedby="'tt-location'">
                                                    <v-icon small color="grey darken-2" aria-hidden="true">mdi-help-circle-outline</v-icon>
                                                </v-btn>
                                            </template>
                                            <span id="tt-location" role="tooltip">
                                                The "Location" field specifies the geographical location of the target audience and which search results will be analyzed for optimization purposes.
                                            </span>
                                        </v-tooltip>
                                    </div>

                                    <v-select
                                        :disabled="disabled"
                                        rounded
                                        v-model="langloc"
                                        :items="textMap"
                                        item-text="location_text"
                                        item-value="location_code"
                                        prepend-inner-icon="mdi-map-marker"
                                        return-object
                                        filled
                                        attach
                                        :aria-describedby="'tt-location'">
                                        <template v-slot:prepend-inner>
                                            <v-icon aria-hidden="true">mdi-map-marker</v-icon>
                                        </template>
                                    </v-select>
                                </div>
                            </v-col>
                        </v-row>

                        <v-row no-gutters>
                            <!-- DEVICE -->
                            <v-col class="cols-6">
                                <div class="qgreycard my-0">
                                    <span class="lbl" style="font-size: 14px !important">
                                        DEVICE
                                        <v-tooltip max-width="250" top open-on-focus>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn v-on="on" v-bind="attrs" icon type="button" x-small class="pl-0 mr-auto tooltipicon" aria-label="Help: Device" :aria-describedby="'tt-device'">
                                                    <v-icon small color="grey darken-2" aria-hidden="true">mdi-help-circle-outline</v-icon>
                                                </v-btn>
                                            </template>
                                            <span id="tt-device" role="tooltip">
                                                The "Device" field specifies the type of device that the target audience is likely to use to access the content. This choice can impact the analyzed
                                                competitors as well.
                                            </span>
                                        </v-tooltip>
                                    </span>

                                    <v-chip-group
                                        @change="changeDevice"
                                        class="py-0"
                                        color="purple darken-3"
                                        column
                                        show-arrows
                                        v-model="devicefilter"
                                        mandatory
                                        next-icon="mdi-chevron-right"
                                        prev-icon="mdi-chevron-left"
                                        role="radiogroup"
                                        aria-label="Select device type">
                                        <v-chip
                                            :disabled="disabled"
                                            v-for="(devicetype, i) in devicetypes"
                                            :key="i"
                                            filter
                                            close-icon="mdi-close-circle-outline"
                                            filter-icon="mdi-check-circle"
                                            role="radio"
                                            :aria-checked="devicefilter === i"
                                            :aria-label="devicetype.label"
                                            :tabindex="0">
                                            {{ devicetype.label }}
                                        </v-chip>
                                    </v-chip-group>
                                </div>
                            </v-col>

                            <!-- COMPETITORS -->
                            <v-col class="cols-6">
                                <div class="qgreycard my-0">
                                    <span class="lbl" style="font-size: 14px !important">
                                        COMPETITORS
                                        <v-tooltip max-width="250" top open-on-focus>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn
                                                    v-on="on"
                                                    v-bind="attrs"
                                                    icon
                                                    type="button"
                                                    x-small
                                                    class="pl-0 mr-auto tooltipicon"
                                                    aria-label="Help: Competitors"
                                                    :aria-describedby="'tt-competitors'">
                                                    <v-icon small color="grey darken-2" aria-hidden="true">mdi-help-circle-outline</v-icon>
                                                </v-btn>
                                            </template>
                                            <span id="tt-competitors" role="tooltip">
                                                The "Competitors" field specifies which competitors will be selected as a reference for your content project.
                                            </span>
                                        </v-tooltip>
                                    </span>

                                    <v-chip-group
                                        @change="changeDepth"
                                        class="py-0"
                                        color="cyan darken-4"
                                        column
                                        show-arrows
                                        v-model="resultFilter"
                                        mandatory
                                        next-icon="mdi-chevron-right"
                                        prev-icon="mdi-chevron-left"
                                        role="radiogroup"
                                        aria-label="Select competitor depth">
                                        <v-chip
                                            :disabled="disabled"
                                            v-for="(resultChip, i) in resultChips"
                                            :key="i"
                                            filter
                                            close-icon="mdi-close-circle-outline"
                                            filter-icon="mdi-check-circle"
                                            role="radio"
                                            :aria-checked="resultFilter === i"
                                            :aria-label="resultChip.label"
                                            :tabindex="0">
                                            {{ resultChip.label }}
                                        </v-chip>
                                    </v-chip-group>
                                </div>
                            </v-col>
                        </v-row>

                        <!-- Optional own URL -->
                        <v-slide-x-transition>
                            <div v-if="addOwnURL" class="qgreycard">
                                <div class="lbl pb-1">WEBSITE URL</div>
                                <v-text-field class="my-2" rounded filled v-model="project.url" hint="e.g., https://www.quaro.io/keyword-recherche" persistent-hint label="Your website URL" />
                            </div>
                        </v-slide-x-transition>

                        <!-- Fast mode -->
                        <v-row v-if="!edit">
                            <v-col class="pl-5">
                                <v-switch v-model="project.setup.fastmode" class="pb-2" color="primary" hide-details label="Fast Mode" :aria-describedby="'tt-fastmode'" />
                                <v-tooltip max-width="250" top open-on-focus>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn v-on="on" v-bind="attrs" x-small outlined color="primary" type="button" aria-describedby="tt-fastmode"> What is Fast Mode? </v-btn>
                                    </template>
                                    <div id="tt-fastmode" role="tooltip">
                                        <strong style="font-size: 18px">Fast Mode</strong>
                                        <p>Speed up the process and skip most setup steps to go directly to the Editor, Briefing, or Analysis.</p>
                                        <p><strong>Note: You can change the settings later on.</strong></p>
                                    </div>
                                </v-tooltip>
                            </v-col>
                        </v-row>
                    </div>
                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script>
    export default {
        props: {
            project: Object,
            edit: Boolean
        },
        data() {
            return {
                devicetypes: [
                    { selected: true, label: "Mobile" },
                    { selected: false, label: "Desktop" }
                ],
                devicefilter: [0],
                resultFilter: 1,
                resultChips: [
                    { depth: 5, label: "Top 5" },
                    { depth: 10, label: "Top 10" },
                    { depth: 20, label: "Top 20" },
                    { depth: 30, label: "Top 30" }
                ],
                chooseoption: 1,
                addOwnURL: false,
                // langloc: { language_code: "de", location_code: "2276" },
                langloc_items: [
                    { language_code: "de", location_code: "2276" },
                    { language_code: "de", location_code: "2756" },
                    { language_code: "de", location_code: "2040" },
                    { language_code: "en", location_code: "2826" },
                    { language_code: "en", location_code: "2840" },
                    { language_code: "it", location_code: "2380" },
                    // { language_code: "sv", location_code: "2752" },
                    { language_code: "fr", location_code: "2250" },
                    // { language_code: "uk", location_code: "2804" },
                    { language_code: "es", location_code: "2724" },
                    { language_code: "pt", location_code: "2620" }
                    // { language_code: "no", location_code: "2578" },
                    // { language_code: "nl", location_code: "2528" }
                ],
                textMap: [
                    { location_text: "Germany", language_code: "de", location_code: "2276" },
                    { location_text: "Switzerland (de)", language_code: "de", location_code: "2756" },
                    { location_text: "Austria (de)", language_code: "de", location_code: "2040" },
                    { location_text: "United Kingdom", language_code: "en", location_code: "2826" },
                    { location_text: "United States", language_code: "en", location_code: "2840" },
                    { location_text: "Italy", language_code: "it", location_code: "2380" },
                    // { location_text: "Sweden", language_code: "sv", location_code: "2752" },
                    { location_text: "France", language_code: "fr", location_code: "2250" },
                    // { location_text: "Ukraine", language_code: "uk", location_code: "2804" },
                    { location_text: "Spain", language_code: "es", location_code: "2724" },
                    { location_text: "Portugal", language_code: "pt", location_code: "2620" }
                    // { location_text: "Norway", language_code: "no", location_code: "2578" },
                    // { location_text: "Finland", language_code: "fi", location_code: "2564" },
                    // { location_text: "Netherlands", language_code: "nl", location_code: "2528" }
                ],
                tipps: [
                    {
                        id: "title",
                        text: "Lorem ipsum dolor sit amet consectetur adipisicing elit. Qui expedita a ratione eius, dignissimos veniam, cumque tempora sapiente harum praesentium voluptates tenetur repellat dolores unde atque animi, culpa quae minima!"
                    },
                    {
                        id: "main_keyword",
                        text: "Lorem ipsum dolor sit amet consectetur adipisicing elit. Qui expedita a ratione eius, dignissimos veniam, cumque tempora sapiente harum praesentium voluptates tenetur repellat dolores unde atque animi, culpa quae minima!"
                    },
                    {
                        id: "device",
                        text: "Lorem ipsum dolor sit amet consectetur adipisicing elit. Qui expedita a ratione eius, dignissimos veniam, cumque tempora sapiente harum praesentium voluptates tenetur repellat dolores unde atque animi, culpa quae minima!"
                    }
                ],
                visibleTipp: null
            }
        },
        methods: {
            validateMainKeyword() {
                console.log("validate")
                this.project.main_keyword = this.$helpers.replaceSpecialCharacters([this.project.main_keyword])[0].toLowerCase().trim()
            },
            changeDevice(val) {
                this.project.device = this.devicetypes[val].label
            },
            changeDepth(val) {
                this.project.setup.depth = this.resultChips[val]
            },
            showTipp(tippID) {
                if (!this.visibleTipp) {
                    this.visibleTipp = this.tipps.find((tipp) => tipp.id == tippID)
                } else {
                    this.visibleTipp = null
                    setTimeout(() => {
                        this.visibleTipp = this.tipps.find((tipp) => tipp.id == tippID)
                    }, 500)
                }
            }
        },
        computed: {
            disabled() {
                console.log(this.project)
                if (this.project) {
                    console.log(this.project.setup)
                    return this.project.setup.finished || this.project.setup.step >= 2
                }
                return true
            },
            langloc: {
                get() {
                    return this.project.langloc
                },
                set(val) {
                    this.project.langloc = this.langloc_items.find((entry) => entry.location_code == val.location_code)
                }
            }
            // devicefilter: {
            //     get() {
            //         return this.project.device
            //     },
            //     set(val) {
            //         this.project.device = this.devicetypes[this.devicefilter].label
            //     }
            // },
            // resultFilter: {
            //     get() {
            //         return this.project.depth
            //     },
            //     set(val) {
            //         this.project.depth = this.resultChips[this.resultFilter]
            //     }
            // }
        },
        mounted() {
            this.addOwnURL = this.project.url ? true : false
        },
        watch: {
            project() {
                this.addOwnURL = this.project.url ? true : false
            }
        }
    }
</script>

<style lang="scss">
    .infocard {
        height: 100%;
        background-color: rgb(199, 199, 199);
        border-radius: 15px;
    }
    .lbl {
        font-size: 14px !important;
        font-weight: 600;
    }
    .formarea {
        /* border-radius: 10px;
        padding: 5px 20px;
        box-shadow: 0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%); */
        /* background: linear-gradient(
            90deg,
            var(--v-background_gradient_1-base) 0%,
            var(--v-background_gradient_2-base) 26%,
            var(--v-background_gradient_3-base) 50%,
            var(--v-background_gradient_4-base) 75%,
            var(--v-background_gradient_5-base) 100%
        ); */
        background: #fafbfa;
    }
    .tippicon {
        display: flex;
        height: 100%;
    }
    .tipp {
        display: flex;
        height: 100%;
    }
    .tooltipicon {
        margin-bottom: 3px;
        margin-left: 1px;
    }
    .choose > div > :is(div, button) {
        max-width: 300px;
        display: flex;
        padding: 0 30px;
        background-color: #f6f6f6;
        border-radius: 10px;
        &:hover {
            background-color: #dddcdc;
            cursor: pointer;
        }
        &.active,
        &[aria-pressed="true"] {
            background-color: var(--v-primary-base) !important;
            color: white;
        }
    }

    /* Dark theme Variante ebenfalls für beide Elemente */
    .theme--dark .choose > div > :is(div, button) {
        background-color: #1c1c1c;
    }
    .chooseimage {
        max-width: 40%;
        padding: 10px;
    }
    .choosetitle {
        display: flex;
        margin: auto;
    }
</style>
<style>
    /* use Vuetify primary (fallback: #1976d2) */

    /* Choice cards */
    button.choice-btn:focus,
    button.choice-btn:focus-visible {
        outline: 3px solid var(--v-primary-base);
        outline-offset: 3px;
    }

    /* Chips */
    .v-chip:focus,
    .v-chip:focus-visible {
        outline: 3px solid var(--v-primary-base);
        outline-offset: 3px;
    }

    /* Select / Autocomplete / Text fields */
    .v-input .v-input__slot:focus-within {
        outline: 3px solid var(--v-primary-base);
        outline-offset: 3px;
        border-radius: 12px; /* optional, passt zu rounded */
    }
</style>
